import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { scrollTo } from "scroll-js";
import styled, { createGlobalStyle } from "styled-components";
import { useUserSettings } from "@americanbible/uikit";

import { isMobileDevice } from "../../../utils/mobile";
import { CHAPTER_MODES } from "./ChapterMode";

const AudioSyncStyles = createGlobalStyle`
  ${({ currentAudioVerseId }) => `
  .verse-span,
  .s1 {
    opacity: .4 !important;
  }

  .verse-span[data-verse-id="${currentAudioVerseId}"] {
    opacity: 1 !important;
  }

  .ChapterView__Copyright-sc-1rbyc55-5 {
    padding-bottom: ${window.innerHeight}px;
  }
  `}
`;

const Arrow = styled.div`
  width: 0;
  height: 0;
  border-style: solid;
  z-index: 9999;
  position: absolute;
  top: 0;
  transition: transform 75ms ease;
  ${({ theme, scriptDirection }) => {
    const color = theme.darkMode ? "white" : theme.primary;

    if (theme.scriptDirection === "ltr") {
      return `
        left: 0;
        border-color: transparent transparent transparent ${color};
        border-width: 6px 0 6px 7px;
      `;
    } else {
      return `
        right: 0;
        border-color: transparent ${color} transparent transparent;
        border-width: 6px 7px 6px 0;
        `;
    }
  }}
`;

class ArrowPortal extends React.Component {
  render() {
    const portal = document.getElementById("current-chapter-col");
    if (!portal) return null;
    return ReactDOM.createPortal(this.props.children, portal);
  }
}

export const getScrollElement = isMobile =>
  isMobile ? document.getElementById("current-chapter") : window;

const getArrowTop = ({ currentAudioVerseElement, fontSize }) => {
  if (!currentAudioVerseElement) return 0;

  const verseOffsetTop = currentAudioVerseElement.offsetTop;
  const scriptureOffsetTop = document.querySelector(
    "#current-chapter #scripture"
  ).offsetTop;
  const scripturePadding = 8;
  const distanceToMiddleOfText = (fontSize / 2) * 1.5; // note: line-height is 1.5x larger than font-size

  // result ending in .5px or greater feel like the arrow is pushed
  // too far down, rounding down keeps the rendered result from rounding up
  return Math.floor(
    verseOffsetTop +
      scriptureOffsetTop +
      distanceToMiddleOfText -
      scripturePadding
  );
};

export const ChapterScroller = ({
  mode,
  currentAudioVerseElement,
  currentAudioVerseId,
  incrementScrollLocks,
  decrementScrollLocks
}) => {
  const { fontSize } = useUserSettings();
  const isMobile = isMobileDevice();
  const scrollElement = getScrollElement(isMobile);

  // keep current verse is view if we're in audio sync mode
  useEffect(() => {
    if (currentAudioVerseElement) {
      let top;

      const verseTop = currentAudioVerseElement.getBoundingClientRect().top;
      if (isMobile) {
        top =
          scrollElement.scrollTop +
          verseTop -
          122 /* AppBar height 72px + desired padding of 50px */;
      } else {
        top =
          scrollElement.scrollY +
          verseTop -
          Math.min(window.innerHeight * 0.15, 100); // 15% of the window height but no more than 100px
      }

      if (mode === CHAPTER_MODES.AUDIO_SYNC) {
        incrementScrollLocks();
        scrollTo(scrollElement, {
          top,
          behavior: "smooth",
          duration: 300
        }).then(() => {
          // NOTE: requestAnimationFrame is used here because scrollTo resolves
          // it's promise after calling it's last scroll step which itself it
          // async. If you did into the lib's code they do this within a
          // requestAnimationFrame, so we know if we do the same, out decrement
          // of the lock count wont happen before the scroll has 100% completed
          window.requestAnimationFrame(() => {
            decrementScrollLocks();
          });
        });
      }
    }
  }, [
    currentAudioVerseId,
    mode,
    currentAudioVerseElement,
    decrementScrollLocks,
    incrementScrollLocks,
    isMobile,
    scrollElement
  ]);

  return (
    <>
      {mode !== CHAPTER_MODES.DEFAULT && (
        <ArrowPortal>
          <Arrow
            style={{
              transform: `translateY(${getArrowTop({
                currentAudioVerseElement,
                fontSize
              })}px)`
            }}
          />
        </ArrowPortal>
      )}
      {mode === CHAPTER_MODES.AUDIO_SYNC && (
        <AudioSyncStyles currentAudioVerseId={currentAudioVerseId} />
      )}
    </>
  );
};
